// components/Layout/PhrasesFloatBox.jsx
import React, { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { parseSRTTimeToSeconds } from "../../utils/formatTime";
import { safeGetItem } from "../../utils/safeStorage";
import { getPortalContainer } from "../../utils/portalContainer";
import { useVideo } from "../../context/VideoContext";
import { useSelectedSubtitles } from "../../context/SelectedSubtitlesContext";
import { LearningItemRow, WordsDetailBox } from "./Sidebar";
import "./Sidebar.css";

// باکس ۳ ثانیه قبل از شروع زیرنویس ظاهر و ۲ ثانیه بعد از پایان مخفی می‌شود
const PRE_SHOW_SECONDS = 3;
const POST_HIDE_SECONDS = 2;

const readFontScale = (key) => {
  const saved = safeGetItem(key);
  const parsed = saved ? parseInt(saved, 10) : 100;
  return Number.isFinite(parsed)
    ? Math.min(300, Math.max(50, parsed))
    : 100;
};

const PhrasesFloatBox = ({ isSidebarCollapsed }) => {
  const {
    currentTime,
    videoRef,
    handleSeek,
    pauseVideo,
    isIOS,
    isFullscreen,
    learningData,
    learningSubOneID,
    autoLoadedSub1Id,
    subtitle1Json,
  } = useVideo();
  const { getActiveSubtitle } = useSelectedSubtitles();

  const [detailItem, setDetailItem] = useState(null);

  // تنظیمات از localStorage (فقط وقتی سایدبار جمع است این باکس رندر می‌شود)
  const showEnabled = safeGetItem("sidebarShowPhrasesNoSidebar") !== "false";
  const showCompleteOnly = safeGetItem("sidebarShowCompleteOnly") !== "false";
  const fontScale = readFontScale("sidebarFontScale") / 100;
  const detailFontScale = readFontScale("sidebarDetailFontScale") / 100;

  // آی‌دی زیرنویس اول فعلی (همان منطق سایدبار)
  const subtitle1 = getActiveSubtitle(1);
  const subOneID =
    subtitle1?.subtitlesUploadId ||
    subtitle1?.onlineId ||
    subtitle1?._id ||
    autoLoadedSub1Id ||
    null;

  // داده‌های یادگیری فقط وقتی معتبرند که برای همین زیرنویس اول دریافت شده باشند
  const hasData = !!learningData && learningSubOneID === subOneID;

  // آیا این باکس اصلاً باید کار کند؟ (ویژگی فعال باشد، سایدبار جمع باشد، داده موجود باشد)
  const active = showEnabled && isSidebarCollapsed && hasData;

  // نگاشت subtitleIndex به آیتم‌های لغت/اصطلاح (با احترام به فیلتر «توضیحات کامل»)
  const itemsByCueKey = useMemo(() => {
    const map = new Map();
    if (!active) return map;
    for (const item of learningData.items || []) {
      if (showCompleteOnly && !(item.cefr && item.cefr !== "Unknown")) {
        continue;
      }
      const key = Number(item.subtitleIndex);
      if (!Number.isFinite(key)) continue;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    return map;
  }, [active, learningData, showCompleteOnly]);

  // هر زیرنویس که پنجره زمانی [شروع-۳ثانیه ... پایان+۲ثانیه] با زمان فعلی تداخل
  // دارد، آیتم‌هایش نمایش داده می‌شود؛ تداخل بین زیرنویس‌ها طبیعی پشتیبانی می‌شود.
  const visibleGroups = useMemo(() => {
    const groups = [];
    if (!active || !subtitle1Json || itemsByCueKey.size === 0) return groups;
    for (let i = 0; i < subtitle1Json.length; i++) {
      const cue = subtitle1Json[i];
      const key = Number(cue.id);
      const items = itemsByCueKey.get(Number.isFinite(key) ? key : i);
      if (!items || items.length === 0) continue;
      const start = parseSRTTimeToSeconds(cue.startTime);
      const end = parseSRTTimeToSeconds(cue.endTime);
      if (
        currentTime >= start - PRE_SHOW_SECONDS &&
        currentTime <= end + POST_HIDE_SECONDS
      ) {
        groups.push({ start, items });
      }
    }
    return groups.sort((a, b) => a.start - b.start);
  }, [active, subtitle1Json, itemsByCueKey, currentTime]);

  const visibleItems = useMemo(
    () => visibleGroups.flatMap((group) => group.items),
    [visibleGroups],
  );

  // هنگام توقف ویدئو، باکس‌ها پابرجا می‌مانند و فقط دکمه بستن نمایان می‌شود
  const isPaused = !!videoRef.current?.paused;

  // اصطلاحاتی که کاربر با ضربدر به‌صورت دستی پنهان کرده است (تا پایان همین جلسه)
  const [hiddenKeys, setHiddenKeys] = useState(() => new Set());

  const visibleFiltered = useMemo(
    () =>
      visibleItems
        .map((item, index) => ({ item, index }))
        .filter(
          ({ item, index }) =>
            !hiddenKeys.has(`${item.subtitleIndex}-${index}-${item.phrase}`),
        ),
    [visibleItems, hiddenKeys],
  );

  // باکس شناور فقط وقتی نمایش داده می‌شود که: ویژگی فعال، سایدبار جمع، داده لغات
  // موجود و حداقل یک زیرنویس در پنجره نمایش باشد. (توقف ویدئو باکس را مخفی نمی‌کند)
  const showFloating = active && visibleFiltered.length > 0;

  const handleOpenDetail = (item) => {
    pauseVideo();
    setDetailItem(item);
  };

  const handleCloseDetail = () => setDetailItem(null);

  const handleDismiss = (item, index) => {
    setHiddenKeys((prev) => {
      const next = new Set(prev);
      next.add(`${item.subtitleIndex}-${index}-${item.phrase}`);
      return next;
    });
  };

  const handlePlayItem = (item) => {
    if (typeof item.timestamp !== "number") return;
    handleSeek(item.timestamp);
    if (videoRef.current) {
      videoRef.current.currentTime = item.timestamp;
      videoRef.current
        .play()
        .catch(() => console.warn("video play blocked"));
    }
  };

  return (
    <>
      {showFloating && (
        <div
          className="phrases-float-box"
          style={{ "--sidebar-font-scale": String(fontScale) }}
        >
          {visibleFiltered.map(({ item, index }) => (
            <div className="phrase-float-card" key={`${item.subtitleIndex}-${index}-${item.phrase}`}>
              {isPaused && (
                <button
                  type="button"
                  className="phrase-float-close"
                  onClick={() => handleDismiss(item, index)}
                  title="پنهان کردن این اصطلاح"
                >
                  <i className="fas fa-times"></i>
                </button>
              )}
              <LearningItemRow
                item={item}
                onPlay={handlePlayItem}
                onOpenDetail={handleOpenDetail}
              />
            </div>
          ))}
        </div>
      )}

      {detailItem &&
        createPortal(
          <WordsDetailBox
            item={detailItem}
            onClose={handleCloseDetail}
            fontScale={detailFontScale}
          />,
          getPortalContainer(isIOS, isFullscreen)
        )}
    </>
  );
};

export default PhrasesFloatBox;